import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Bath, BedDouble, Calendar, Ruler } from "lucide-react";
import { ListingHeader } from "./listing/ListingHeader";
import { ListingFooter } from "./listing/ListingFooter";
import { MobileContactBar } from "./listing/MobileContactBar";
import { AgentCard } from "./listing/AgentCard";
import { SponsorCard } from "./listing/SponsorCard";
import { PhotoLightbox } from "./listing/PhotoLightbox";
import { ShowingRequestDialog } from "./listing/ShowingRequestDialog";
import { AdvertiserInquiryDialog } from "./listing/AdvertiserInquiryDialog";
import { DownloadPhotosDialog } from "./listing/DownloadPhotosDialog";
import { useListingData, listingFileUrl } from "./listing/useListingData";
import { useHashScroll } from "./listing/useHashScroll";
import { useIsMobile } from "../hooks/use-mobile";
import { HeroSection, PhotosSection, MortgageSection, PremierAgentsSection, LocalServicesSection, PromoSection, SponsorBannerSlot } from "./listing/PropertyWebsiteHomeSections";
import { LocalExpertsButton } from "./listing/LocalExpertsButton";

const protectImage = (event) => {
  if (event.target instanceof Element && event.target.closest("img, video")) event.preventDefault();
};

function propertyStats(listing) {
  return [
    listing.beds && { icon: BedDouble, label: "Beds", value: listing.beds },
    listing.baths && { icon: Bath, label: "Baths", value: listing.baths },
    listing.sqft && { icon: Ruler, label: "Sq Ft", value: listing.sqft },
  ].filter(Boolean);
}

export default function PropertyWebsiteHome() {
  const { slug, preview, listing, error } = useListingData();
  const isMobile = useIsMobile();
  const [heroIndex, setHeroIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showingOpen, setShowingOpen] = useState(false);
  const [inquireAd, setInquireAd] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const images = useMemo(() => (listing?.files || []).filter((file) => file.is_image), [listing]);
  const ordered = useMemo(() => {
    if (!images.length) return [];
    const cover = images.find((file) => file.id === listing?.cover_file_id);
    return cover ? [cover, ...images.filter((file) => file.id !== cover.id)] : images;
  }, [images, listing]);
  const fileUrl = (file) => listingFileUrl(listing, slug, preview, file);
  const withQS = (path) => (preview ? `${path}?preview=1` : path);

  useEffect(() => {
    if (!listing || lightboxIndex !== null || showingOpen) return undefined;
    const interval = setInterval(() => setHeroIndex((i) => (ordered.length ? (i + 1) % ordered.length : 0)), 7000);
    return () => clearInterval(interval);
  }, [listing, ordered.length, lightboxIndex, showingOpen]);

  useHashScroll(Boolean(listing));

  if (error) return <main data-testid="public-listing-error" className="min-h-screen bg-white grid place-items-center p-8 text-center"><h1 className="text-xl font-serif-display text-[#202833]">{error}</h1></main>;
  if (!listing) return <main data-testid="public-listing-loading" className="min-h-screen grid place-items-center bg-white font-mono text-sm text-[#667085]">Loading property website…</main>;

  const contact = listing.contact;
  const hero = ordered[heroIndex];
  const brandLabel = contact?.name || listing.full_address || listing.project_name;
  const stats = propertyStats(listing);
  const sponsors = listing.sponsors || {};
  const showSponsors = listing.sponsors_enabled !== false;
  const allLocalServiceAds = showSponsors ? (sponsors.local_service_card || []) : [];
  const sponsorsByCategory = {};
  for (const ad of allLocalServiceAds) { (sponsorsByCategory[ad.category] ||= []).push(ad); }
  const topBannerAd = showSponsors ? sponsors.top_banner?.[0] : null;
  const midBannerAd = showSponsors ? sponsors.mid_banner?.[0] : null;
  const lowerBannerAd = showSponsors ? sponsors.lower_banner?.[0] : null;
  const sidebarAds = showSponsors ? (sponsors.sidebar_card || []).slice(0, 3) : [];

  return (
    <main data-testid="public-property-website" onContextMenuCapture={protectImage} onDragStartCapture={protectImage} className="min-h-screen bg-white text-[#202833]">
      {preview && <div data-testid="public-listing-preview-banner" className="bg-[#C7A361] text-[#111820] text-center text-xs font-mono uppercase tracking-[0.14em] py-2">Admin preview — this is how the page will look to visitors</div>}
      <ListingHeader slug={slug} brandLabel={brandLabel} preview={preview} onRequestShowing={() => setShowingOpen(true)} onMenuOpenChange={setMobileMenuOpen} />

      <SponsorBannerSlot advertiser={topBannerAd} onInquire={setInquireAd} compact={isMobile} className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 pt-5" />

      <HeroSection listing={listing} ordered={ordered} hero={hero} heroIndex={heroIndex} setHeroIndex={setHeroIndex}
        fileUrl={fileUrl} stats={stats} onViewPhoto={setLightboxIndex} onRequestShowing={() => setShowingOpen(true)} />

      <section className={`mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 py-10 sm:py-14 grid gap-6 lg:gap-8 items-start ${sidebarAds.length > 0 ? "lg:grid-cols-[280px_1fr_320px]" : "lg:grid-cols-[280px_1fr]"}`}>
        <div>
          <AgentCard contact={contact} onRequestShowing={() => setShowingOpen(true)} />
          <button data-testid="public-listing-schedule-showing-mobile" onClick={() => setShowingOpen(true)} className="sm:hidden mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#C7A361] text-[#111820] text-sm font-semibold uppercase tracking-[0.08em] py-3.5 hover:brightness-95 transition"><Calendar size={15} />Schedule a Showing</button>
        </div>
        <div>
          <h2 className="font-serif-display text-lg text-[#202833]">About This Property</h2>
          {listing.website_description && <p data-testid="public-listing-description" className="mt-3 text-sm text-[#667085] leading-relaxed">{listing.website_description}</p>}
          <Link to={withQS(`/listing/${slug}/details`)} data-testid="public-listing-view-details-link" className="mt-4 inline-block text-sm font-medium text-[#C7A361] hover:underline">View Full Details →</Link>
        </div>
        {sidebarAds.length > 0 && (
          <div data-testid="public-listing-featured-sponsors" className="hidden lg:block space-y-5 lg:sticky lg:top-24">
            <h2 className="font-serif-display text-lg text-[#202833]">Featured Sponsors</h2>
            <div className="grid grid-cols-2 gap-4">
              {sidebarAds.map((ad) => <SponsorCard key={ad.id} advertiser={ad} onInquire={setInquireAd} compact />)}
            </div>
          </div>
        )}
      </section>

      <section className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 pb-14 sm:pb-20 space-y-12 sm:space-y-16">
        <PhotosSection ordered={ordered} fileUrl={fileUrl} onViewPhoto={setLightboxIndex} onDownloadAll={() => setDownloadOpen(true)} />

        <SponsorBannerSlot advertiser={midBannerAd} onInquire={setInquireAd} />

        <MortgageSection ads={sponsorsByCategory.financing || []} onInquire={setInquireAd} isMobile={isMobile} />

        <PremierAgentsSection ads={sponsorsByCategory.real_estate_agent || []} slug={slug} withQS={withQS} />

        <LocalServicesSection sponsorsByCategory={sponsorsByCategory} slug={slug} withQS={withQS} onInquire={setInquireAd} isMobile={isMobile} />
      </section>

      <PromoSection contact={contact} onRequestShowing={() => setShowingOpen(true)} />

      <SponsorBannerSlot advertiser={lowerBannerAd} onInquire={setInquireAd} className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 py-10" />

      <ListingFooter slug={slug} brandLabel={brandLabel} brokerage={contact?.company} preview={preview} />
      <MobileContactBar phone={contact?.phone} onRequestShowing={() => setShowingOpen(true)} hidden={lightboxIndex !== null || showingOpen || Boolean(inquireAd) || mobileMenuOpen} />
      <LocalExpertsButton slug={slug} sponsorsByCategory={sponsorsByCategory} />

      <AnimatePresence>
        {lightboxIndex !== null && (
          <PhotoLightbox photos={ordered.map((f) => ({ id: f.id, url: fileUrl(f) }))} index={lightboxIndex} onIndexChange={setLightboxIndex} onClose={() => setLightboxIndex(null)} />
        )}
      </AnimatePresence>
      <ShowingRequestDialog open={showingOpen} onClose={() => setShowingOpen(false)} slug={slug} agentName={contact?.name} address={listing.full_address || listing.project_name} />
      <AdvertiserInquiryDialog advertiser={inquireAd} slug={slug} onClose={() => setInquireAd(null)} />
      <DownloadPhotosDialog open={downloadOpen} onClose={() => setDownloadOpen(false)} slug={slug} />
    </main>
  );
}
